import React from "react"

import { Button, useCopyToClipboardFeedback } from "@coval/ui"
import { Check, Copy } from "lucide-react"

import { readOnlyInteractiveProps } from "./readOnlyGuard"

interface ReadOnlyCopyButtonProps {
  value: string
  label: string
  className?: string
}

/**
 * Copy control for read-only regions. A disabled fieldset disables every native `<button>` inside it, so this renders `CopyButton`'s look on a focusable `role="button"` span marked with `readOnlyInteractiveProps`.
 */
export function ReadOnlyCopyButton({
  value,
  label,
  className,
}: ReadOnlyCopyButtonProps) {
  const { copied, copy } = useCopyToClipboardFeedback()
  const activate = () => {
    void copy(value)
  }

  return (
    <Button
      asChild
      icon
      variant="secondary"
      tooltip={copied ? "Copied!" : label}
      tooltipProps={{ side: "top", forceOpen: copied }}
      aria-label={label}
      className={className}
    >
      <span
        role="button"
        tabIndex={0}
        {...readOnlyInteractiveProps}
        onClick={activate}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            activate()
          }
        }}
      >
        {copied ? (
          <Check className="size-3.5 text-coval-success-text" />
        ) : (
          <Copy className="size-3.5" />
        )}
      </span>
    </Button>
  )
}
